export type ServiceBorough = 'manhattan' | 'brooklyn' | 'queens' | 'outside' | 'unknown';

export interface ServiceAreaCheckResult {
  status: 'serviceable' | 'outside' | 'prompt';
  borough?: 'Manhattan' | 'Brooklyn' | 'Queens';
  title: string;
  message: string;
}

/**
 * Detects whether a given city and/or ZIP code falls within Regency House Cleaning's
 * exclusive NYC operating territory (Manhattan, Brooklyn, Queens).
 */
export function detectBorough(inputCity: string = '', inputZip: string = ''): ServiceBorough {
  const rawZip = (inputZip || '').trim();
  const rawCity = (inputCity || '').trim().toLowerCase();

  // 1. Check 5-digit ZIP Code
  if (rawZip.length >= 5) {
    const zipNum = parseInt(rawZip.slice(0, 5), 10);

    // Manhattan ZIPs: 10001 - 10282
    if (zipNum >= 10001 && zipNum <= 10282) {
      return 'manhattan';
    }

    // Brooklyn ZIPs: 11201 - 11256
    if (zipNum >= 11201 && zipNum <= 11256) {
      return 'brooklyn';
    }

    // Queens ZIPs: 11004, 11005, 11101-11109, 11351-11386, 11411-11439, 11691-11697
    if (
      zipNum === 11004 ||
      zipNum === 11005 ||
      (zipNum >= 11101 && zipNum <= 11109) ||
      (zipNum >= 11351 && zipNum <= 11386) ||
      (zipNum >= 11411 && zipNum <= 11439) ||
      (zipNum >= 11691 && zipNum <= 11697)
    ) {
      return 'queens';
    }

    // Any other 5-digit ZIP code is outside the 3 boroughs
    return 'outside';
  }

  // 2. Check City / Neighborhood Name
  if (rawCity) {
    const manhattanKeywords = [
      'manhattan', 'new york', 'ny', 'nyc', 'new york city',
      'upper east side', 'ues', 'upper west side', 'uws',
      'tribeca', 'soho', 'chelsea', 'midtown', 'west village',
      'greenwich village', 'east village', 'gramercy', 'flatiron',
      'financial district', 'fidi', 'battery park', 'harlem',
      'hudson yards', 'hells kitchen', 'murray hill', 'noho', 'nolita'
    ];
    if (manhattanKeywords.some(k => rawCity.includes(k))) {
      return 'manhattan';
    }

    const brooklynKeywords = [
      'brooklyn', 'brooklyn heights', 'dumbo', 'park slope',
      'williamsburg', 'greenpoint', 'cobble hill', 'carroll gardens',
      'boerum hill', 'fort greene', 'clinton hill', 'bushwick',
      'bed-stuy', 'bedford stuyvesant', 'crown heights', 'prospect heights',
      'red hook', 'bay ridge', 'flatbush', 'sunset park', 'gowanus',
      'downtown brooklyn'
    ];
    if (brooklynKeywords.some(k => rawCity.includes(k))) {
      return 'brooklyn';
    }

    const queensKeywords = [
      'queens', 'astoria', 'flushing', 'long island city', 'lic', 'jamaica',
      'forest hills', 'bayside', 'ridgewood', 'sunnyside', 'jackson heights',
      'woodside', 'elmhurst', 'corona', 'whitestone', 'fresh meadows',
      'kew gardens', 'middle village', 'maspeth', 'howard beach', 'ozone park',
      'rosedale', 'rockaway', 'rego park'
    ];
    if (queensKeywords.some(k => rawCity.includes(k))) {
      return 'queens';
    }

    if (
      ['the bronx', 'bronx', 'staten island', 'yonkers', 'jersey city', 'hoboken', 'newark', 'new jersey', 'connecticut', 'westchester', 'long island', 'nassau', 'suffolk'].some(
        k => rawCity.includes(k)
      )
    ) {
      return 'outside';
    }
  }

  return 'unknown';
}

/**
 * Convenience helper to verify if a location is within the serviceable boroughs.
 */
export function isNycServiceable(inputCity: string = '', inputZip: string = ''): boolean {
  const borough = detectBorough(inputCity, inputZip);
  return borough === 'manhattan' || borough === 'brooklyn' || borough === 'queens';
}

/**
 * Checks a freeform query (ZIP code, borough, or neighborhood name) and provides
 * a formatted verification response for UI display.
 */
export function checkServiceLocation(query: string): ServiceAreaCheckResult {
  const trimmed = (query || '').trim();
  if (!trimmed) {
    return {
      status: 'prompt',
      title: 'Check Your NYC Building or Neighborhood',
      message: 'Enter your 5-digit ZIP code or neighborhood to confirm immediate service availability.'
    };
  }

  // If input contains digits, check as ZIP code
  const hasDigits = /\d{5}/.test(trimmed);
  const borough = hasDigits ? detectBorough('', trimmed) : detectBorough(trimmed, '');

  if (borough === 'manhattan') {
    return {
      status: 'serviceable',
      borough: 'Manhattan',
      title: 'Confirmed: Full Service Available in Manhattan',
      message: 'We actively service all Manhattan residences with dedicated stewards, building COI coverage, and concierge key coordination.'
    };
  }

  if (borough === 'brooklyn') {
    return {
      status: 'serviceable',
      borough: 'Brooklyn',
      title: 'Confirmed: Full Service Available in Brooklyn',
      message: 'We actively service your Brooklyn residence with certified heritage care, brownstone specialists, and punctual local teams.'
    };
  }

  if (borough === 'queens') {
    return {
      status: 'serviceable',
      borough: 'Queens',
      title: 'Confirmed: Full Service Available in Queens',
      message: 'We actively service your Queens neighborhood with hospital-grade HEPA sanitization and white-glove residential protocols.'
    };
  }

  return {
    status: 'outside',
    title: 'Outside Current Operating Territory',
    message: 'Regency House Cleaning exclusively operates within Manhattan, Brooklyn, and Queens in New York City to guarantee unmatched service quality.'
  };
}

/**
 * Calculates Monthly Recurring Revenue (MRR) and Annual Contract Value (ACV)
 * matching the Zoho CRM Blueprint Deluge script:
 * - Weekly: Amount * 4.333
 * - Bi-Weekly: Amount * 2.167
 * - Every 3 Weeks: Amount * 1.444
 * - Monthly: Amount * 1.0
 * - One-time: 0.0
 * ACV = (MRR * 12) + firstCleanFee
 */
export function calculateSeniorMRR(
  amount: number,
  frequency: string = 'Bi-Weekly',
  firstCleanFee: number = 0
): { mrr: number; acv: number } {
  const perClean = Number(amount) || 0;
  const firstClean = Number(firstCleanFee) || 0;
  let mrr = 0;

  if (frequency === 'Weekly') {
    mrr = perClean * 4.333;
  } else if (frequency === 'Bi-Weekly' || frequency === 'Biweekly') {
    mrr = perClean * 2.167;
  } else if (frequency === 'Every 3 Weeks') {
    mrr = perClean * 1.444;
  } else if (frequency === 'Monthly') {
    mrr = perClean * 1.0;
  } else {
    mrr = 0.0;
  }

  const roundedMrr = Math.round(mrr * 100) / 100;
  const roundedAcv = Math.round(((roundedMrr * 12) + firstClean) * 100) / 100;

  return { mrr: roundedMrr, acv: roundedAcv };
}

/**
 * Calculates a dynamic Instant Remote Estimate based on residence dimensions and recurring frequency.
 */
export function estimateSeniorCleaningRate(params: {
  sqFt?: number;
  bedrooms?: number;
  bathrooms?: number;
  frequency?: 'Weekly' | 'Bi-Weekly' | 'Every 3 Weeks' | 'Monthly' | 'One-Time';
}): {
  perCleanRate: number;
  firstCleanFee: number;
  mrr: number;
  acv: number;
  discountPercentage: number;
} {
  const sqFt = Math.max(400, Number(params.sqFt) || 1200);
  const beds = Math.max(1, Number(params.bedrooms) || 2);
  const baths = Math.max(1, Number(params.bathrooms) || 1.5);
  const freq = params.frequency || 'Bi-Weekly';

  // Base tier from sq footage
  let basePrice = 160;
  if (sqFt > 750 && sqFt <= 1200) {
    basePrice = 210;
  } else if (sqFt > 1200 && sqFt <= 1800) {
    basePrice = 270;
  } else if (sqFt > 1800 && sqFt <= 2500) {
    basePrice = 330;
  } else if (sqFt > 2500) {
    basePrice = 330 + Math.round((sqFt - 2500) * 0.09);
  }

  // Bed/Bath complexity additions
  const extraBeds = Math.max(0, beds - 1) * 20;
  const extraBaths = Math.max(0, baths - 1) * 30;
  const standardGross = basePrice + extraBeds + extraBaths;

  // Frequency discounts for predictable recurring service
  let discountPercentage = 0;
  if (freq === 'Weekly') {
    discountPercentage = 20;
  } else if (freq === 'Bi-Weekly') {
    discountPercentage = 15;
  } else if (freq === 'Every 3 Weeks') {
    discountPercentage = 10;
  } else if (freq === 'Monthly') {
    discountPercentage = 5;
  }

  const perCleanRate = Math.round(standardGross * (1 - discountPercentage / 100));
  // First clean / deep reset is ~1.35x of standard rate for initial fall prevention & deep sanitization
  const firstCleanFee = Math.round(standardGross * 1.35);

  const { mrr, acv } = calculateSeniorMRR(perCleanRate, freq, firstCleanFee);

  return {
    perCleanRate,
    firstCleanFee,
    mrr,
    acv,
    discountPercentage,
  };
}
